import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, X } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { inputClass } from "./Contact";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingModal = () => {
  const { bookingOpen, setBookingOpen } = useSite();
  const [form, setForm] = useState({ name: "", practice_name: "", email: "", phone: "", specialty: "", preferred_time: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!bookingOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setBookingOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bookingOpen, setBookingOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/booking`, form);
      toast.success("Discovery call requested — we'll confirm a time by email shortly.");
      setForm({ name: "", practice_name: "", email: "", phone: "", specialty: "", preferred_time: "", message: "" });
      setBookingOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {bookingOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-brand-ink/60 backdrop-blur-sm"
          onClick={() => setBookingOpen(false)}
          data-testid="booking-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white dark:bg-[#0D1434] border border-slate-100 dark:border-white/10 p-8 shadow-2xl"
            data-testid="booking-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Book a discovery call"
          >
            <button
              data-testid="booking-modal-close"
              onClick={() => setBookingOpen(false)}
              aria-label="Close"
              className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors duration-300"
            >
              <X size={18} />
            </button>
            <div className="w-12 h-12 rounded-2xl bg-brand-navy dark:bg-brand-sky flex items-center justify-center mb-5">
              <CalendarCheck size={20} className="text-white dark:text-brand-ink" />
            </div>
            <h3 className="font-display text-2xl font-semibold text-slate-900 dark:text-white">Book a Discovery Call</h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Thirty minutes. Your workflow, your EHR, your numbers — no pitch deck.</p>
            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-input-name" required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} aria-label="Name" />
                <input data-testid="booking-input-practice-name" placeholder="Practice name" value={form.practice_name} onChange={(e) => setForm({ ...form, practice_name: e.target.value })} className={inputClass} aria-label="Practice name" />
                <input data-testid="booking-input-email" required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} aria-label="Email" />
                <input data-testid="booking-input-phone" type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} aria-label="Phone" />
                <input data-testid="booking-input-specialty" placeholder="Specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className={inputClass} aria-label="Specialty" />
                <input data-testid="booking-input-preferred-time" placeholder="Preferred time (e.g. Tue 2 PM ET)" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={inputClass} aria-label="Preferred time" />
              </div>
              <textarea data-testid="booking-input-message" rows={3} placeholder="Anything we should know?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} resize-none`} aria-label="Notes" />
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="w-full rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)] disabled:opacity-60 transition-shadow duration-300"
              >
                {sending ? "Requesting…" : "Request Discovery Call"}
              </button>
              <p className="text-[11px] text-center text-slate-400" data-testid="booking-phi-warning">
                HIPAA-conscious by design — please don't include patient information in this form.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
export default BookingModal;
